import { i18n } from 'i18n';
import {
  makeCreateMutation,
  makeDeleteMutation,
  makeGetQuery,
  makeListQuery,
  makeUpdateMutation
} from './useApi';
import {
  CustomReportBindingGroupDto,
  CustomReportRequirementStage,
  CustomReportRequirementStatusDto,
  CustomReportStopeBindingDto,
  CustomReportSubmissionDto,
  CustomReportTargetType,
  CustomReportTemplateDto,
  CustomReportTemplateForm,
  CustomReportTemplateRequirementsDto,
  FileParameter,
  SaveCustomReportSubmissionForm
} from './api';

export const CUSTOM_REPORT_TEMPLATES_KEY = 'custom-report-templates';
export const CUSTOM_REPORT_REQUIREMENTS_KEY = 'custom-report-requirements';
export const CUSTOM_REPORT_TEMPLATE_FILE_KEY = 'custom-report-template-file';
export const CUSTOM_REPORT_SUBMISSIONS_KEY = 'custom-report-submissions';
export const CUSTOM_REPORT_STOPE_BINDINGS_KEY = 'custom-report-stope-bindings';
export const CUSTOM_REPORT_BINDING_CATALOG_KEY = 'custom-report-binding-catalog';
export const CUSTOM_REPORT_RESOLVED_VALUES_KEY = 'custom-report-resolved-values';
export const CUSTOM_REPORT_STOPE_NOTE_TEMPLATE_KEY = 'custom-report-stope-note-template';

// The template designated as the project's stope note ('' when none). Used to decide whether to
// generate the custom stope note (vs the native one) and to drive the "Stope Note" toggle state.
export const useStopeNoteTemplateIdQuery = makeGetQuery<string, string>(
  CUSTOM_REPORT_STOPE_NOTE_TEMPLATE_KEY,
  (api, projectId) => () => api.customReports.getStopeNoteTemplateId(projectId),
);

export const useCustomReportTemplatesQuery = makeListQuery<string, CustomReportTemplateDto[]>(
  CUSTOM_REPORT_TEMPLATES_KEY,
  (api, projectId) => () => api.customReports.getTemplates(projectId),
);

export interface CustomReportTemplateMutation {
  projectId: string;
  templateId?: string;
  form: CustomReportTemplateForm;
}

export const useCreateCustomReportTemplateMutation = makeCreateMutation<string, CustomReportTemplateMutation>(
  CUSTOM_REPORT_TEMPLATES_KEY,
  (api) => (mutation) => api.customReports.createTemplate(mutation.projectId, mutation.form),
  () => i18n._('Custom report template created'),
);

export const useUpdateCustomReportTemplateMutation = makeUpdateMutation<unknown, CustomReportTemplateMutation>(
  CUSTOM_REPORT_TEMPLATES_KEY,
  CUSTOM_REPORT_TEMPLATES_KEY,
  (api) => (mutation) => api.customReports.updateTemplate(mutation.projectId, mutation.templateId ?? '', mutation.form),
  () => i18n._('Custom report template updated'),
  [CUSTOM_REPORT_REQUIREMENTS_KEY],
);

export interface CustomReportTemplateDeleteMutation {
  projectId: string;
  templateId: string;
  name: string;
}

export const useDeleteCustomReportTemplateMutation = makeDeleteMutation<void, CustomReportTemplateDeleteMutation>(
  CUSTOM_REPORT_TEMPLATES_KEY,
  CUSTOM_REPORT_TEMPLATES_KEY,
  (api) => (mutation) => api.customReports.deleteTemplate(mutation.projectId, mutation.templateId),
  (mutation) => i18n._('Custom report template').concat(` ${mutation.name} `).concat(i18n._('deleted')),
  (mutation) => mutation.projectId,
  [CUSTOM_REPORT_REQUIREMENTS_KEY],
);

export interface UploadCustomReportTemplateFileMutation {
  projectId: string;
  templateId: string;
  file: FileParameter;
}

export const useUploadCustomReportTemplateFileMutation = makeCreateMutation<CustomReportTemplateDto, UploadCustomReportTemplateFileMutation>(
  CUSTOM_REPORT_TEMPLATES_KEY,
  (api) => (mutation) => api.customReports.uploadTemplateFile(mutation.projectId, mutation.templateId, mutation.file),
  () => i18n._('Template file uploaded'),
  [CUSTOM_REPORT_TEMPLATE_FILE_KEY],
);

export interface CustomReportTemplateFileQuery {
  projectId: string;
  templateId: string;
}

export const useCustomReportTemplateFileQuery = makeGetQuery<Blob, CustomReportTemplateFileQuery>(
  CUSTOM_REPORT_TEMPLATE_FILE_KEY,
  // Generated client returns a FileResponse; unwrap to the Blob the callers download.
  (api, params) => () => api.customReports.downloadTemplateFile(params.projectId, params.templateId).then((file) => file.data),
);

export interface CustomReportRequirementsQuery {
  projectId: string;
  targetType: CustomReportTargetType;
  targetId: string;
}

export const useCustomReportRequirementsQuery = makeGetQuery<CustomReportRequirementStatusDto[], CustomReportRequirementsQuery>(
  CUSTOM_REPORT_REQUIREMENTS_KEY,
  (api, params) => () => api.customReports.getRequirements(params.projectId, params.targetType, params.targetId),
);

export interface CustomReportStopeBindingsQuery {
  projectId: string;
  stopeId: string;
}

export const useCustomReportStopeBindingsQuery = makeGetQuery<CustomReportStopeBindingDto[], CustomReportStopeBindingsQuery>(
  CUSTOM_REPORT_STOPE_BINDINGS_KEY,
  (api, params) => () => api.customReports.getStopeBindings(params.projectId, params.stopeId),
);

export interface SetCustomReportStopeBindingMutation {
  projectId: string;
  stopeId: string;
  templateId: string;
  stages: CustomReportRequirementStage[];
}

export const useSetCustomReportStopeBindingMutation = makeUpdateMutation<unknown, SetCustomReportStopeBindingMutation>(
  CUSTOM_REPORT_STOPE_BINDINGS_KEY,
  CUSTOM_REPORT_STOPE_BINDINGS_KEY,
  (api) => (mutation) => api.customReports.setStopeBinding(
    mutation.projectId,
    mutation.stopeId,
    mutation.templateId,
    { stages: mutation.stages },
  ),
  () => i18n._('Custom report requirements updated'),
  [CUSTOM_REPORT_REQUIREMENTS_KEY, CUSTOM_REPORT_TEMPLATES_KEY],
);

export const CUSTOM_REPORT_TEMPLATE_REQUIREMENTS_KEY = 'custom-report-template-requirements';

export interface CustomReportTemplateRequirementsQuery {
  projectId: string;
  templateId: string;
}

// The template's requirement scope (all stopes vs an explicit stope set), for the template dialog.
export const useCustomReportTemplateRequirementsQuery = makeGetQuery<CustomReportTemplateRequirementsDto, CustomReportTemplateRequirementsQuery>(
  CUSTOM_REPORT_TEMPLATE_REQUIREMENTS_KEY,
  (api, params) => () => api.customReports.getTemplateRequirements(params.projectId, params.templateId),
);

export interface SetCustomReportTemplateRequirementsMutation {
  projectId: string;
  templateId: string;
  stages: CustomReportRequirementStage[];
  allStopes: boolean;
  stopeIds: string[];
}

export const useSetCustomReportTemplateRequirementsMutation = makeUpdateMutation<unknown, SetCustomReportTemplateRequirementsMutation>(
  CUSTOM_REPORT_TEMPLATE_REQUIREMENTS_KEY,
  CUSTOM_REPORT_TEMPLATE_REQUIREMENTS_KEY,
  (api) => (mutation) => api.customReports.setTemplateRequirements(
    mutation.projectId,
    mutation.templateId,
    { stages: mutation.stages, allStopes: mutation.allStopes, stopeIds: mutation.stopeIds },
  ),
  () => i18n._('Custom report requirements updated'),
  [CUSTOM_REPORT_REQUIREMENTS_KEY, CUSTOM_REPORT_STOPE_BINDINGS_KEY, CUSTOM_REPORT_TEMPLATES_KEY],
);

export interface CustomReportBindingCatalogQuery {
  projectId: string;
  targetType: CustomReportTargetType;
}

export const useCustomReportBindingCatalogQuery = makeGetQuery<CustomReportBindingGroupDto[], CustomReportBindingCatalogQuery>(
  CUSTOM_REPORT_BINDING_CATALOG_KEY,
  (api, params) => () => api.customReports.getBindingCatalog(params.projectId, params.targetType),
);

export interface CustomReportResolvedValuesQuery {
  projectId: string;
  templateId: string;
  targetType: CustomReportTargetType;
  targetId: string;
  includeRoutePoints?: boolean;
  // JSON-serialised route points (keyed by pour id), so the payload is a stable primitive in the query
  // key. When it changes (routes finish computing) the query refetches and route-derived figures fill.
  routePoints?: string;
}

export const useCustomReportResolvedValuesQuery = makeGetQuery<Record<string, string>, CustomReportResolvedValuesQuery>(
  CUSTOM_REPORT_RESOLVED_VALUES_KEY,
  (api, params) => () => api.customReports.getResolvedValues(
    params.projectId,
    params.templateId,
    { includeRoutePoints: params.includeRoutePoints, routePoints: params.routePoints },
    params.targetType,
    params.targetId,
  ),
);

export const useCustomReportSubmissionsQuery = makeListQuery<string, CustomReportSubmissionDto[]>(
  CUSTOM_REPORT_SUBMISSIONS_KEY,
  (api, projectId) => () => api.customReports.getSubmissions(projectId),
);

export interface SaveCustomReportSubmissionMutation {
  projectId: string;
  templateId: string;
  targetType: CustomReportTargetType;
  targetId: string;
  form: SaveCustomReportSubmissionForm;
}

export const useSaveCustomReportSubmissionMutation = makeUpdateMutation<unknown, SaveCustomReportSubmissionMutation>(
  CUSTOM_REPORT_REQUIREMENTS_KEY,
  CUSTOM_REPORT_REQUIREMENTS_KEY,
  (api) => (mutation) => api.customReports.saveSubmission(
    mutation.projectId,
    mutation.templateId,
    mutation.form,
    mutation.targetType,
    mutation.targetId,
  ),
  (mutation) => (mutation.form.complete ? i18n._('Custom report completed') : i18n._('Custom report saved')),
  [CUSTOM_REPORT_TEMPLATES_KEY, CUSTOM_REPORT_SUBMISSIONS_KEY],
);
